import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadStepFile } from "@/components/upload/UploadStepFile";
import {
  UploadStepMetadata,
  type MetadataValues,
} from "@/components/upload/UploadStepMetadata";
import { UploadDuplicateDialog } from "@/components/upload/UploadDuplicateDialog";
import { useUpload } from "@/hooks/useUpload";
import { getSystemSettings, listMeetingTypes } from "@/api/lookups";
import type { UUID } from "@/api/types";

export interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const METADATA_FORM_ID = "upload-metadata-form";

export function UploadDialog({ open, onOpenChange }: UploadDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  // Last submitted field values, kept so the «Загрузить как новую» action from
  // the duplicate dialog can re-run the upload with force_new without asking
  // the operator to re-enter anything.
  const [metadata, setMetadata] = useState<MetadataValues | null>(null);
  const [metaValid, setMetaValid] = useState(false);
  const { state, startUpload, reset, clearDuplicate } = useUpload();

  // Meeting types stay eager — they're a closed enum with no search.
  const types = useQuery({
    queryKey: ["meeting-types"],
    queryFn: listMeetingTypes,
    enabled: open,
  });
  const settings = useQuery({
    queryKey: ["system-settings"],
    queryFn: getSystemSettings,
    enabled: open,
  });

  // Reset dialog state whenever it closes.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setMetadata(null);
      setMetaValid(false);
      reset();
    }
  }, [open, reset]);

  const config = useMemo(
    () => ({
      // ADR-020: до 4 ГБ через chunked path; <500 МБ идут legacy multipart.
      // Dialog принимает оба размера, useUpload автоматически выбирает path.
      maxBytes: settings.data?.upload_max_bytes ?? 4 * 1024 * 1024 * 1024,
      allowedExtensions: settings.data?.upload_allowed_extensions ?? [
        "txt",
        "doc",
        "docx",
        "json",
        "mp3",
        "wav",
        "m4a",
        "ogg",
        "flac",
        "aac",
        "mp4",
        "mov",
        "mkv",
        "webm",
      ],
    }),
    [settings.data],
  );

  const doUpload = useCallback(
    async (values: MetadataValues, forceNew: boolean) => {
      if (file === null) return;
      const result = await startUpload({
        file,
        fields: {
          client_id: values.client_id as UUID,
          meeting_type_id: values.meeting_type_id as UUID,
          participants: values.participants as UUID[],
          // folder-management-v3 §2.3 — pass through only when the picker
          // actually selected a folder; admin without a member list can
          // legitimately submit with no value and let the BE fall back to
          // active-folder.
          ...(values.folder_id !== undefined && values.folder_id !== ""
            ? { folder_id: values.folder_id as UUID }
            : {}),
        },
        forceNew,
      });
      if (result !== null) {
        onOpenChange(false);
        await navigate({ to: `/meetings/${result.meeting_id}` });
      }
    },
    [file, navigate, onOpenChange, startUpload],
  );

  // Metadata form submit (validated by react-hook-form) → start the upload.
  const handleSubmit = useCallback(
    (values: MetadataValues) => {
      setMetadata(values);
      void doUpload(values, false);
    },
    [doUpload],
  );

  const handleForceNew = useCallback(() => {
    if (metadata === null) return;
    clearDuplicate();
    void doUpload(metadata, true);
  }, [metadata, clearDuplicate, doUpload]);

  // TM-4cfb7824 — while a file is being hashed or uploaded (especially the
  // chunked path for >500 MB) silently closing the dialog would cancel the
  // in-flight work and lose all typed metadata. The base `DialogContent`
  // already blocks click-outside (see ui/dialog.tsx); this guard adds the
  // matching protection for Escape and any other route into
  // `onOpenChange(false)`.
  const busy = state.hashing || state.uploading;
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && busy) return;
      onOpenChange(next);
    },
    [busy, onOpenChange],
  );

  const canSubmit = file !== null && metaValid && !busy;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("meetings.upload.title")}</DialogTitle>
          <DialogDescription>{t("meetings.upload.subtitle")}</DialogDescription>
        </DialogHeader>

        {state.duplicate !== null ? (
          <UploadDuplicateDialog
            duplicate={state.duplicate}
            onOpenExisting={async (id) => {
              onOpenChange(false);
              await navigate({ to: `/meetings/${id}` });
            }}
            onForceNew={handleForceNew}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <div className="space-y-4">
            <UploadStepFile
              initialFile={file}
              config={config}
              disabled={busy}
              onFileAccepted={setFile}
            />
            <UploadStepMetadata
              formId={METADATA_FORM_ID}
              meetingTypes={types.data ?? []}
              defaultValues={metadata ?? undefined}
              onSubmit={handleSubmit}
              onValidityChange={setMetaValid}
            />

            {state.hashing ? (
              <p className="text-sm" data-testid="upload-hashing">
                {t("meetings.upload.computingHash")}
                {state.progressPercent !== null
                  ? ` ${state.progressPercent}%`
                  : null}
              </p>
            ) : null}
            {state.uploading && state.progressPercent !== null ? (
              <p className="text-sm" data-testid="upload-progress">
                {t("meetings.upload.uploadProgress", {
                  percent: state.progressPercent,
                })}
              </p>
            ) : null}
            {state.error !== null ? (
              <p role="alert" className="text-sm text-destructive">
                {t(state.error, { defaultValue: state.error })}
              </p>
            ) : null}

            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                form={METADATA_FORM_ID}
                disabled={!canSubmit}
              >
                {busy
                  ? t("meetings.upload.submitting")
                  : t("meetings.upload.submit")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
